import { Button, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils'
import { format } from '@/utils/date-fns'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useState } from 'react'
import { RESPOND_TO_EVENT_MUTATION } from '../mutations'
import type {
  EventNode,
  EventRegistrationStatus,
  RespondToEventMutation,
  RespondToEventMutationVariables,
} from '../types.graphql'
import { EventQuestionField } from './event-question-field'

interface Props {
  event: EventNode
}

type Answers = Record<string, string>

const answersOf = (event: EventNode): Answers =>
  Object.fromEntries(
    (event.myRegistration?.answers ?? []).map(answer => [
      answer.question.id,
      answer.value,
    ])
  )

/** The employee's own answer: the two buttons, and what the event asks of them. */
export const EventResponseForm: React.FC<Props> = ({ event }) => {
  const translations = useTranslate({
    yourAnswer: 'events.your-answer',
    attending: 'events.attending',
    declined: 'events.declined',
    noAnswerYet: 'events.no-answer-yet',
    closed: 'events.registration-closed',
    answerBefore: ['events.answer-before', { date: '' }],
    note: 'common.comment',
    notePlaceholder: 'events.note-placeholder',
    save: 'common.save',
    saved: 'events.answer-saved',
    error: 'events.answer-error',
  })

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const registration = event.myRegistration

  const [status, setStatus] = useState<EventRegistrationStatus | null>(
    registration?.status ?? null
  )
  const [answers, setAnswers] = useState<Answers>(answersOf(event))
  const [note, setNote] = useState(registration?.note ?? '')

  // An answer changed elsewhere (another tab, a refetch) wins over what is in
  // the form, which is only ever the same answer until the user edits it.
  const [seenAnswer, setSeenAnswer] = useState(registration?.updatedAt ?? null)
  if ((registration?.updatedAt ?? null) !== seenAnswer) {
    setSeenAnswer(registration?.updatedAt ?? null)
    setStatus(registration?.status ?? null)
    setAnswers(answersOf(event))
    setNote(registration?.note ?? '')
  }

  const [respond, { loading }] = useMutation<
    RespondToEventMutation,
    RespondToEventMutationVariables
  >(RESPOND_TO_EVENT_MUTATION, {
    onCompleted: () => addToast('success', translations.saved),
    onError: onErrorAuto(translations.error),
  })

  if (!event.registrationOpen)
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 px-4 py-3 text-sm">
        <span className="font-semibold text-stone-900">
          {translations.closed}
        </span>
        <span className="text-stone-600">
          {registration
            ? registration.status === 'ATTENDING'
              ? translations.attending
              : translations.declined
            : translations.noAnswerYet}
        </span>
      </div>
    )

  const attending = status === 'ATTENDING'
  const missingRequired = event.questions.some(
    question => question.required && !answers[question.id]
  )

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-stone-200 px-4 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-stone-900">
          {translations.yourAnswer}
        </span>

        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={attending ? 'default' : 'outline'}
            onClick={() => setStatus('ATTENDING')}
          >
            {translations.attending}
          </Button>

          <Button
            type="button"
            size="sm"
            variant={status === 'DECLINED' ? 'default' : 'outline'}
            onClick={() => setStatus('DECLINED')}
          >
            {translations.declined}
          </Button>
        </div>

        <span className="text-xs text-stone-500">
          {translations.answerBefore({
            date: format(new Date(event.registrationClosesAt), 'PPp'),
          })}
        </span>
      </div>

      {attending && event.questions.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-8">
          {event.questions.map(question => (
            <EventQuestionField
              key={question.id}
              question={question}
              value={answers[question.id] ?? ''}
              onChange={value =>
                setAnswers(current => ({ ...current, [question.id]: value }))
              }
            />
          ))}
        </div>
      )}

      <div className={cn('flex flex-col gap-1', !status && 'hidden')}>
        <span className="text-xs font-semibold uppercase text-stone-500">
          {translations.note}
        </span>
        <Textarea
          value={note}
          rows={2}
          placeholder={translations.notePlaceholder}
          className="max-w-2xl"
          onChange={setNote}
        />
      </div>

      <Button
        type="button"
        size="sm"
        className="w-full sm:w-auto sm:self-end"
        disabled={!status || loading || (attending && missingRequired)}
        onClick={() => {
          if (!status) return
          void respond({
            variables: {
              event: event.id,
              status,
              note,
              answers: event.questions.map(question => ({
                question: question.id,
                value: answers[question.id] ?? '',
              })),
            },
          })
        }}
      >
        {translations.save}
      </Button>
    </div>
  )
}
